import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Search, Bell, Globe, MoreVertical, Trash2, ExternalLink, Loader2,
  Sparkles, Wand2, Link2, FolderOpen, Coins, BarChart3, Rocket,
  ArrowRight, Layout, Settings, CreditCard, Layers,
  Gift, Zap, Send, MessageSquare, Circle, Database, TrendingUp, ArrowUpRight,
  Menu, X, Home as HomeIcon,
} from 'lucide-react';
import { OrbisMarkSVG } from '@/components/OrbisLogo';
import CreditMeter from '@/components/CreditMeter';
import UserMenu from '@/components/UserMenu';

// Dashboard components
import HeroWelcome from '@/components/dashboard/HeroWelcome';
import OrbisPulse from '@/components/dashboard/OrbisPulse';
import StatCard from '@/components/dashboard/StatCard';
import { capture } from '@/lib/analytics';
import ProjectSpotlight from '@/components/dashboard/ProjectSpotlight';
import ExpandingSearch from '@/components/dashboard/ExpandingSearch';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Card accents ── */
const CARD_ACCENTS = {
  magic: { bg: 'var(--orbis-blue)', text: '#1A1A1A', muted: '#1A1A1A', badge: '#1A1A1A20', badgeText: '#1A1A1A' },
  clone: { bg: 'var(--orbis-amber)', text: '#1A1A1A', muted: '#1A1A1A', badge: '#1A1A1A20', badgeText: '#1A1A1A' },
  template: { bg: 'var(--orbis-green)', text: '#1A1A1A', muted: '#1A1A1A', badge: '#1A1A1A20', badgeText: '#1A1A1A' },
  classic: { bg: '#1A1A1A', text: 'var(--orbis-warm)', muted: '#5A5855', badge: '#FFFFFF14', badgeText: '#8A8882' },
};

function getCardAccent(project) {
  if (project.magic_mode) return CARD_ACCENTS.magic;
  if (project.clone_mode) return CARD_ACCENTS.clone;
  if (project.template_mode) return CARD_ACCENTS.template;
  return CARD_ACCENTS.classic;
}

/* ── ProjectCard (used in Projects tab grid) ── */
function ProjectCard({ project, onDelete, onNavigate, accent }) {
  const a = accent || CARD_ACCENTS.classic;
  return (
    <div
      onClick={() => onNavigate(project)}
      className="cursor-pointer group transition-all duration-200 hover:-translate-y-1"
      style={{
        height: '180px', borderRadius: '14px', position: 'relative', overflow: 'hidden',
        background: a.bg, border: '0.5px solid rgba(255,255,255,0.08)',
      }}
    >
      <div style={{ padding: '24px', position: 'relative', zIndex: 1 }}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {project.magic_mode && <span style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '3px 10px', borderRadius: '100px', background: a.badge, color: a.badgeText }}>Magic</span>}
            {project.clone_mode && <span style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '3px 10px', borderRadius: '100px', background: a.badge, color: a.badgeText }}>Clone</span>}
            {project.template_mode && <span style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '3px 10px', borderRadius: '100px', background: a.badge, color: a.badgeText }}>Template</span>}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button onClick={e => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" style={{ background: 'none', border: 'none', padding: 4 }}>
                <MoreVertical className="w-4 h-4" style={{ color: a.muted }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)', borderRadius: '10px' }}>
              {project.custom_domain && (
                <DropdownMenuItem onSelect={() => window.open(`https://${project.custom_domain}`, '_blank')} className="flex items-center gap-2 cursor-pointer" style={{ color: 'var(--orbis-t2)', fontSize: '13px' }}>
                  <ExternalLink className="w-3.5 h-3.5" /> Visit Site
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => onDelete(project.id)} className="flex items-center gap-2 cursor-pointer" style={{ color: '#f87171', fontSize: '13px' }}>
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <h3 style={{ fontSize: '16px', fontWeight: 500, color: a.text, letterSpacing: '-0.01em', marginBottom: '4px' }}>{project.name}</h3>
        <p style={{ fontSize: '12px', fontWeight: 300, color: a.muted, opacity: 0.7 }}>{project.custom_domain || 'AI-generated website project'}</p>
      </div>
      <div className="absolute flex items-center justify-between w-full px-6" style={{ bottom: '16px' }}>
        <span style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', color: a.muted, opacity: 0.6 }}>{project.custom_domain ? `${project.custom_domain}` : 'No domain'}</span>
        <span style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', color: a.muted, opacity: 0.5 }}>Updated {new Date(project.updated_at || project.created_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

/* ── Time-ago helper ── */
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}


export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const credits = useCredits();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDomain, setNewProjectDomain] = useState('');
  const [newProjectRefUrl, setNewProjectRefUrl] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [magicDialogOpen, setMagicDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashStats, setDashStats] = useState(null);

  useEffect(() => { fetchProjects(); }, []);

  // Fetch real dashboard stats (visits, bandwidth, AI generations)
  useEffect(() => {
    const fetchDashStats = async () => {
      try {
        const r = await axios.get(`${API_URL}/dashboard/stats`);
        setDashStats(r.data);
      } catch (e) {
        // Silently fail — cards show 0 / fallback when dashStats is null
      }
    };
    fetchDashStats();
  }, []);

  const fetchProjects = async () => {
    try { const r = await axios.get(`${API_URL}/projects`); setProjects(r.data); }
    catch { toast.error('Failed to load projects'); }
    finally { setIsLoading(false); }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) { toast.error('Please enter a project name'); return; }
    setIsCreating(true);
    try {
      const r = await axios.post(`${API_URL}/projects`, { name: newProjectName, custom_domain: newProjectDomain || null, clone_mode: true, reference_url: newProjectRefUrl.trim() || null });
      setProjects([r.data, ...projects]); setNewProjectName(''); setNewProjectDomain(''); setNewProjectRefUrl(''); setDialogOpen(false);
      capture('project_created', { mode: 'clone', project_id: r.data.id });
      toast.success('Clone project created!'); navigate(`/clone/${r.data.id}`);
    } catch { toast.error('Failed to create project'); } finally { setIsCreating(false); }
  };

  const handleCreateMagicProject = async () => {
    if (!newProjectName.trim()) { toast.error('Please enter a project name'); return; }
    setIsCreating(true);
    try {
      const r = await axios.post(`${API_URL}/magic/projects`, { name: newProjectName, brief: { company_name: newProjectName, industry: '', audience: '', vibe: '', pages: ['home'], notes: '', preset: null } });
      setProjects([{ ...r.data, magic_mode: true }, ...projects]); setNewProjectName(''); setMagicDialogOpen(false);
      capture('project_created', { mode: 'magic', project_id: r.data.id });
      toast.success('Magic project created!'); navigate(`/magic/${r.data.id}`);
    } catch { toast.error('Failed to create magic project'); } finally { setIsCreating(false); }
  };

  const handleNavigateToProject = (project) => {
    if (project.magic_mode) navigate(`/magic/${project.id}`);
    else if (project.clone_mode) navigate(`/clone/${project.id}`);
    else if (project.template_mode) navigate(`/templates/${project.id}/edit`);
    else navigate(`/workspace/${project.id}`);
  };

  const handleDeleteProject = async (projectId) => {
    try { await axios.delete(`${API_URL}/projects/${projectId}`); setProjects(projects.filter(p => p.id !== projectId)); toast.success('Project deleted'); }
    catch { toast.error('Failed to delete project'); }
  };

  const inputStyle = { background: 'var(--orbis-s2)', borderColor: 'var(--orbis-border)', color: 'var(--orbis-t1)' };
  const labelStyle = { color: 'var(--orbis-t2)', fontSize: '11px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' };
  const dialogStyle = { background: 'var(--orbis-s1)', borderColor: 'var(--orbis-border)' };

  const tabs = [
    { id: 'home', label: 'Home' },
    { id: 'projects', label: 'Projects' },
    { id: 'templates', label: 'Templates' },
  ];

  /* ── Demo messages for Co-Pilot chat ── */
  const DEMO_MESSAGES = [
    { role: 'user', text: 'How should I structure the hero section?', time: '11:32 AM' },
    { role: 'ai', text: 'I recommend a 60/40 split layout with the CTA anchored left. This works well for SaaS landing pages.', time: '11:33 AM', code: 'layout: split-hero --ratio 60/40' },
    { role: 'user', text: 'Apply that to my latest project.', time: '11:38 AM' },
    { role: 'ai', text: "Done. I've updated the hero structure in your latest project. Check the Structure stage.", time: '11:38 AM' },
  ];

  /* ── Shared panel content (desktop sidebar + mobile drawer) ── */
  const LeftPanelContent = () => (
    <>
      {/* Co-Pilot — fills available vertical space */}
      <div style={{
        flex: 1,
        background: 'var(--orbis-s1)',
        border: '0.5px solid var(--orbis-s2)',
        borderRadius: 14,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 280,
      }}>
        <div className="flex items-center gap-2" style={{ padding: '14px 16px 12px', borderBottom: '0.5px solid var(--orbis-s2)' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--orbis-positive)', animation: 'pulse-dot 2s ease-in-out infinite' }} />
          <span style={{ fontSize: 11, fontFamily: 'var(--orbis-mono)', fontWeight: 600, color: 'var(--orbis-t3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Orbis Co-Pilot</span>
        </div>
        {/* Context chip — shows which project is active */}
        {projects.length > 0 && (
          <div style={{ padding: '6px 16px 0' }}>
            <span style={{
              display: 'inline-block', fontSize: 10, fontFamily: 'var(--orbis-mono)',
              color: 'var(--orbis-t3)', background: 'var(--orbis-s2)',
              padding: '3px 10px', borderRadius: 100,
              border: '0.5px solid var(--orbis-s3)',
            }}>
              Viewing: {[...projects].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0]?.name}
            </span>
          </div>
        )}
        {/* Chat messages — proper bubble UI */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {DEMO_MESSAGES.map((msg, i) => (
            <div key={i} style={{
              display: 'flex', flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 8,
            }}>
              <div style={{
                background: msg.role === 'ai' ? 'var(--orbis-s1)' : 'var(--orbis-s2)',
                border: '0.5px solid var(--orbis-s2)',
                borderRadius: msg.role === 'ai' ? '12px 12px 12px 2px' : '12px 12px 2px 12px',
                padding: '10px 14px',
                maxWidth: msg.role === 'user' ? '85%' : '80%',
              }}>
                <p style={{
                  fontSize: 13, fontFamily: 'var(--orbis-font)',
                  color: msg.role === 'ai' ? 'var(--orbis-t2)' : 'var(--orbis-warm)',
                  lineHeight: 1.6, margin: 0,
                }}>
                  {msg.text}
                </p>
                {msg.code && (
                  <p style={{
                    fontSize: 11, fontFamily: 'var(--orbis-mono)',
                    color: 'var(--orbis-t4)', marginTop: 6, marginBottom: 0,
                    background: 'var(--orbis-s2)', padding: '5px 10px',
                    borderRadius: 6, display: 'inline-block',
                  }}>
                    {msg.code}
                  </p>
                )}
              </div>
              <span style={{
                fontSize: 10, fontFamily: 'var(--orbis-mono)',
                color: 'var(--orbis-t4)',
                padding: '2px 4px',
              }}>
                {msg.time}
              </span>
            </div>
          ))}
        </div>
        <div style={{ padding: '8px 16px 10px', borderTop: '0.5px solid var(--orbis-s2)', flexShrink: 0 }}>
          {/* Suggestion chips */}
          <div className="flex flex-wrap gap-1.5" style={{ marginBottom: 8 }}>
            {['Review my project', 'Suggest a layout', 'Check credits'].map(chip => (
              <button key={chip} onClick={() => toast.info('Co-Pilot coming soon')}
                className="cursor-pointer hover:opacity-80 transition-opacity"
                style={{
                  fontSize: 10, fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t3)',
                  background: 'transparent', border: '0.5px solid var(--orbis-s3)',
                  borderRadius: 100, padding: '3px 10px',
                }}>
                {chip}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2" style={{ background: 'var(--orbis-s2)', borderRadius: 10, padding: '9px 12px', border: '0.5px solid var(--orbis-border)' }}>
            <input
              type="text" placeholder="Try: 'Generate a pricing section'"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 13, fontFamily: 'var(--orbis-font)', color: 'var(--orbis-warm)' }}
              onKeyDown={e => { if (e.key === 'Enter') toast.info('Co-Pilot coming soon'); }}
            />
            <Send className="w-3.5 h-3.5 cursor-pointer hover:opacity-80 transition-opacity" style={{ color: 'var(--orbis-t4)' }} onClick={() => toast.info('Co-Pilot coming soon')} />
          </div>
        </div>
      </div>

      {/* Upgrade to Pro — persistent banner */}
      <div onClick={() => navigate('/billing')}
        className="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
        style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.08), var(--orbis-s1))', border: '0.5px solid rgba(167,139,250,0.2)', borderRadius: 14, padding: '12px 16px', flexShrink: 0 }}>
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(167,139,250,0.12)' }}>
            <Zap className="w-3.5 h-3.5" style={{ color: '#A78BFA' }} />
          </div>
          <div>
            <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}>Upgrade to Pro</p>
          </div>
        </div>
        <ArrowRight className="w-3 h-3" style={{ color: '#A78BFA' }} />
      </div>

      {/* Share Orbis */}
      <div onClick={() => toast.info('Referral program coming soon')}
        className="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
        style={{ background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)', borderRadius: 14, padding: '12px 16px', flexShrink: 0 }}>
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(143,196,160,0.1)' }}>
            <Gift className="w-3.5 h-3.5" style={{ color: 'var(--orbis-green)' }} />
          </div>
          <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>Share Orbis</p>
        </div>
        <span style={{ fontSize: 10, fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t4)' }}>100 cr/referral</span>
      </div>
    </>
  );

  // Computed stats
  const deployedCount = projects.filter(p => p.status === 'deployed' || p.custom_domain).length;
  const recentActivity = [...projects].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).slice(0, 8);

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--surface-0)' }}>
      {/* Decorative gradient orbs */}
      <div className="pointer-events-none absolute" style={{ width: 800, height: 700, top: -100, left: -100, background: 'radial-gradient(ellipse, rgba(184,204,223,0.06) 0%, transparent 70%)' }} />
      <div className="pointer-events-none absolute" style={{ width: 450, height: 400, top: -50, right: -50, background: 'radial-gradient(ellipse, rgba(184,204,223,0.04) 0%, transparent 70%)' }} />

      {/* ── Navbar ── */}
      <nav className="relative z-10 flex items-center justify-center" style={{ height: 64, background: 'var(--surface-0)', padding: '0 20px', borderBottom: '0.5px solid var(--orbis-border)' }}>
        {/* Left: hamburger (mobile only) + logo — absolutely positioned to stay on left edge */}
        <div className="absolute top-1/2 -translate-y-1/2 flex items-center gap-2.5" style={{ left: 20 }}>
          <button
            className="md:hidden flex items-center justify-center cursor-pointer"
            style={{ background: 'none', border: 'none', padding: '0 2px 0 0' }}
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" style={{ color: 'var(--orbis-t2)' }} />
          </button>
          <OrbisMarkSVG size={22} />
          <span style={{ fontFamily: 'var(--orbis-font)', fontSize: 14, fontWeight: 400, color: 'var(--orbis-t2)' }}>Orbis</span>
        </div>

        {/* Center tabs — hidden on mobile, shown on desktop */}
        <div className="hidden md:flex glass-nav rounded-full items-center" style={{ padding: 4 }}>
          <div className="flex items-center gap-0.5 px-2">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); if (tab.id === 'templates') navigate('/templates'); }}
                className="rounded-full transition-all duration-200"
                style={{ padding: '5px 16px', fontSize: 13, fontWeight: activeTab === tab.id ? 500 : 400, fontFamily: 'var(--orbis-font)', color: activeTab === tab.id ? 'var(--orbis-t1)' : 'var(--orbis-t3)', background: activeTab === tab.id ? 'rgba(250,250,250,0.06)' : 'transparent' }}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right controls — absolutely positioned to stay on right edge */}
        <div className="absolute top-1/2 -translate-y-1/2 flex items-center gap-2 md:gap-3" style={{ right: 20 }}>
          <CreditMeter />
          <div className="hidden sm:flex"><ExpandingSearch projects={projects} onNavigate={handleNavigateToProject} /></div>
          <Bell className="w-3.5 h-3.5 hidden sm:block" style={{ color: 'var(--orbis-t3)' }} />
          <UserMenu />
        </div>
      </nav>

      {/* ── Create Project Dialogs (kept from original) ── */}
      <Dialog open={magicDialogOpen} onOpenChange={setMagicDialogOpen}>
        <DialogContent style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--orbis-warm)', fontWeight: 300 }}>
              <div className="w-8 h-8 rounded-[10px] flex items-center justify-center" style={{ background: 'var(--orbis-blue)' }}><Sparkles className="w-4 h-4" style={{ color: '#1A1A1A' }} /></div>
              Magic Mode
            </DialogTitle>
            <DialogDescription style={{ color: '#5A5855' }}>Create a website with guided AI assistance</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="magic-project-name" style={labelStyle}>Project Name</Label>
              <Input id="magic-project-name" placeholder="My Awesome Website" value={newProjectName} onChange={e => setNewProjectName(e.target.value)} style={inputStyle} />
            </div>
            <Button onClick={handleCreateMagicProject} disabled={isCreating} className="w-full" style={{ background: 'var(--orbis-blue)', color: '#1A1A1A', borderRadius: 100, fontWeight: 400 }}>
              {isCreating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <><Wand2 className="w-4 h-4 mr-2" />Start Magic Mode</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--orbis-warm)', fontWeight: 300 }}>
              <div className="w-8 h-8 rounded-[10px] flex items-center justify-center" style={{ background: 'var(--orbis-amber)' }}><Link2 className="w-4 h-4" style={{ color: '#1A1A1A' }} /></div>
              Clone from reference
            </DialogTitle>
            <DialogDescription style={{ color: '#5A5855' }}>Enter a reference URL and we'll extract its design style</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2"><Label style={labelStyle}>Project Name</Label><Input placeholder="My Awesome Website" value={newProjectName} onChange={e => setNewProjectName(e.target.value)} style={inputStyle} /></div>
            <div className="space-y-2"><Label style={labelStyle}>Reference URL <span style={{ color: '#5A5855', fontWeight: 300, textTransform: 'none', letterSpacing: 'normal' }}>(optional)</span></Label><Input placeholder="stripe.com, linear.app..." value={newProjectRefUrl} onChange={e => setNewProjectRefUrl(e.target.value)} style={inputStyle} /></div>
            <div className="space-y-2"><Label style={labelStyle}>Custom Domain <span style={{ color: '#5A5855', fontWeight: 300, textTransform: 'none', letterSpacing: 'normal' }}>(optional)</span></Label><Input placeholder="mysite.example.com" value={newProjectDomain} onChange={e => setNewProjectDomain(e.target.value)} style={inputStyle} /></div>
            <Button onClick={handleCreateProject} disabled={isCreating} className="w-full" style={{ background: 'var(--orbis-amber)', color: '#1A1A1A', borderRadius: 100, fontWeight: 400 }}>
              {isCreating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <><Link2 className="w-4 h-4 mr-2" />Start Clone Mode</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Mobile drawer (slide-in from left, md:hidden) ── */}
      {sidebarOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 md:hidden"
            style={{ background: 'rgba(0,0,0,0.55)', zIndex: 60 }}
            onClick={() => setSidebarOpen(false)}
          />
          {/* Drawer panel */}
          <motion.div
            initial={{ x: 320 }}
            animate={{ x: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="fixed top-0 right-0 bottom-0 md:hidden"
            style={{
              width: 300, zIndex: 61,
              background: 'var(--surface-0)',
              borderLeft: '0.5px solid var(--orbis-border)',
              display: 'flex', flexDirection: 'column',
              padding: '16px', gap: 12,
              overflowY: 'auto',
            }}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
              <div className="flex items-center gap-2">
                <OrbisMarkSVG size={18} />
                <span style={{ fontFamily: 'var(--orbis-font)', fontSize: 13, fontWeight: 400, color: 'var(--orbis-t2)' }}>Orbis</span>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer' }}
              >
                <X className="w-4 h-4" style={{ color: 'var(--orbis-t3)' }} />
              </button>
            </div>
            <LeftPanelContent />
          </motion.div>
        </>
      )}

      {/* ══════════════ CONTENT AREA ══════════════ */}
      <div className="relative z-10" style={{ padding: 0 }}>

        {activeTab === 'home' ? (
          /* ══════════ HOME TAB — Left panel (420px) + right main content, matching magic mode stage layout ══════════ */
          <div className="md:grid md:grid-cols-[1fr_420px]" style={{ gap: 0, minHeight: 'calc(100vh - 65px)' }}>

            {/* ── LEFT PANEL — main content ── */}
            <div className="pb-24 md:pb-5 flex flex-col" style={{ paddingTop: 32, paddingLeft: 20, paddingRight: 20, minWidth: 0 }}>
              <HeroWelcome user={user} projects={projects} credits={credits} onNavigateToProject={handleNavigateToProject} />

              {!isLoading && (
                <OrbisPulse projects={projects} credits={credits} onNavigateToProject={handleNavigateToProject} />
              )}

              {/* Stats 3×2 */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" style={{ marginBottom: 16 }}>
                <StatCard label="Projects" value={projects.length} context={`${deployedCount} deployed`} color="var(--orbis-blue)" icon={FolderOpen} seed={projects.length} index={0} />
                <StatCard label="Live Sites" value={deployedCount} context={deployedCount > 0 ? 'All resolving' : 'None yet'} color="var(--orbis-positive)" icon={Globe} contextIcon={deployedCount > 0 ? Globe : undefined} seed={deployedCount + 10} index={1} />
                <StatCard label="Total Visits" value={dashStats?.total_visits?.value ?? 0} context={dashStats?.total_visits?.change_percent != null ? `${dashStats.total_visits.change_percent > 0 ? '+' : ''}${dashStats.total_visits.change_percent}% this week` : 'across all sites'} color="#22D3EE" icon={TrendingUp} contextIcon={dashStats?.total_visits?.change_percent > 0 ? ArrowUpRight : undefined} seed={42} index={2} sparklineData={dashStats?.total_visits?.sparkline} />
                <StatCard label="AI Generations" value={dashStats?.ai_generations?.value ?? 0} context={dashStats?.ai_generations?.change_percent != null ? `${dashStats.ai_generations.change_percent > 0 ? '+' : ''}${dashStats.ai_generations.change_percent}% this week` : 'this week'} color="#A78BFA" icon={BarChart3} seed={20} index={3} sparklineData={dashStats?.ai_generations?.sparkline} />
                <StatCard label="Credits" value={credits.balance || 0} context={`${(credits.tier || 'free').toUpperCase()} · ${credits.monthlyCredits || 50}/mo`} color="var(--orbis-amber)" icon={Coins} index={4} progress={credits.monthlyCredits > 0 ? Math.round(((credits.balance || 0) / credits.monthlyCredits) * 100) : 100} />
                <StatCard label="Bandwidth" value={dashStats?.bandwidth?.value_display ?? '0 B'} context={`Of ${dashStats?.bandwidth?.limit_display ?? '1 GB'} monthly limit`} color="#2DD4BF" icon={Database} index={5} progress={dashStats?.bandwidth?.progress_percent ?? 0} sparklineData={dashStats?.bandwidth?.sparkline} />
              </div>

              {/* Project Spotlight */}
              {!isLoading && projects.length > 0 && (
                <ProjectSpotlight projects={projects} onNavigateToProject={handleNavigateToProject} onViewAll={() => setActiveTab('projects')} onDeleteProject={handleDeleteProject} />
              )}

              {/* Start Something New */}
              <div>
                <h2 className="flex items-center gap-2" style={{ fontSize: 13, fontWeight: 600, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                  <span style={{ width: 3, height: 14, borderRadius: 2, background: 'var(--orbis-green)', flexShrink: 0 }} />
                  Start Something New
                </h2>

                {/* Hero: Magic Mode — full width */}
                <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.35 }} whileHover={{ y: -3 }}
                  onClick={() => setMagicDialogOpen(true)} className="cursor-pointer transition-all group"
                  style={{
                    background: 'linear-gradient(135deg, rgba(184,204,223,0.1) 0%, rgba(143,196,160,0.04) 50%, var(--orbis-s1) 100%)',
                    border: '0.5px solid rgba(184,204,223,0.2)', borderRadius: 14,
                    padding: '28px 24px 32px', position: 'relative', overflow: 'hidden', marginBottom: 12,
                  }}>
                  {/* Top gradient line */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--orbis-blue), var(--orbis-green), transparent)' }} />
                  {/* Corner glow */}
                  <div className="opacity-40 group-hover:opacity-70 transition-opacity" style={{
                    position: 'absolute', top: -40, right: -40, width: 160, height: 160,
                    background: 'radial-gradient(circle, rgba(184,204,223,0.15) 0%, transparent 70%)',
                    pointerEvents: 'none',
                  }} />
                  <div className="flex items-center gap-4" style={{ position: 'relative', zIndex: 1 }}>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(184,204,223,0.12)', border: '0.5px solid rgba(184,204,223,0.2)' }}>
                      <Sparkles className="w-5 h-5" style={{ color: 'var(--orbis-blue)' }} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)', marginBottom: 3 }}>Magic Mode</h3>
                      <p style={{ fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)', lineHeight: 1.5 }}>Describe your site and let AI build it — guided 8-stage workflow with design system, content, and deployment</p>
                    </div>
                  </div>
                </motion.div>

                {/* Secondary row: 3 cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { key: 'clone', delay: 0.88, onClick: () => setDialogOpen(true), gradient: 'var(--orbis-amber)', iconBg: 'rgba(212,184,150,0.1)', icon: <Link2 className="w-3.5 h-3.5" style={{ color: 'var(--orbis-amber)' }} />, title: 'Clone Mode', desc: 'Clone any live website in seconds' },
                    { key: 'templates', delay: 0.96, onClick: () => navigate('/templates'), gradient: 'var(--orbis-green)', iconBg: 'rgba(143,196,160,0.1)', icon: <Layers className="w-3.5 h-3.5" style={{ color: 'var(--orbis-green)' }} />, title: 'Templates', desc: 'Browse pre-made designs to customize' },
                    { key: 'domains', delay: 1.04, onClick: () => navigate('/domains'), gradient: '#22D3EE', iconBg: 'rgba(34,211,238,0.1)', icon: <Globe className="w-3.5 h-3.5" style={{ color: '#22D3EE' }} />, title: 'Domains', desc: 'Register & manage your domains' },
                  ].map(card => (
                    <motion.div key={card.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: card.delay, duration: 0.35 }} whileHover={{ y: -3 }}
                      onClick={card.onClick} className="cursor-pointer transition-all"
                      style={{ background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)', borderRadius: 14, padding: '20px', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${card.gradient}, transparent)` }} />
                      <div className="flex items-center gap-2.5" style={{ marginBottom: 8 }}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: card.iconBg }}>{card.icon}</div>
                        <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--orbis-warm)', fontFamily: 'var(--orbis-font)' }}>{card.title}</h3>
                      </div>
                      <p style={{ fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)', lineHeight: 1.6 }}>{card.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── RIGHT PANEL — Co-Pilot + actions (desktop only; mobile uses drawer) ── */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.4 }}
              className="hidden md:flex"
              style={{
                background: 'var(--surface-0)',
                borderLeft: '0.5px solid var(--orbis-border)',
                minHeight: 'calc(100vh - 65px)',
                flexDirection: 'column',
                padding: '20px',
                gap: 12,
              }}>
              <LeftPanelContent />
            </motion.div>

          </div>

        ) : activeTab === 'projects' ? (
          /* ══════════ PROJECTS TAB — Existing Grid ══════════ */
          <div className="px-4 md:px-6 pb-24 md:pb-10">
            <div className="flex flex-wrap items-end justify-between gap-3" style={{ padding: '24px 0 24px' }}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 300, letterSpacing: '-0.02em', color: 'var(--orbis-warm)' }}>Projects</h1>
                <p style={{ fontSize: 13, fontWeight: 300, color: '#8A8882', marginTop: 4 }}>Manage your AI-generated websites</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setMagicDialogOpen(true)} className="flex items-center gap-2 h-11 px-6 cursor-pointer" style={{ background: 'var(--orbis-blue)', color: '#1A1A1A', borderRadius: 100, fontSize: 13, fontWeight: 400, fontFamily: 'var(--orbis-font)', border: 'none' }}>
                  <Sparkles className="w-3.5 h-3.5" /> Magic Mode
                </button>
                <button onClick={() => setDialogOpen(true)} className="flex items-center gap-2 h-11 px-6 cursor-pointer" style={{ background: 'transparent', color: '#8A8882', borderRadius: 100, fontSize: 13, fontWeight: 300, fontFamily: 'var(--orbis-font)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
                  <Link2 className="w-3.5 h-3.5" /> Clone Mode
                </button>
              </div>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 180, borderRadius: 14 }} />)}
              </div>
            ) : projects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20" style={{ borderRadius: 14, border: '0.5px solid rgba(255,255,255,0.08)', background: '#1A1A1A' }}>
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ background: 'var(--orbis-blue)' }}>
                  <Sparkles className="w-8 h-8" style={{ color: '#1A1A1A' }} />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 300, color: 'var(--orbis-warm)', marginBottom: 8 }}>No projects yet</h3>
                <p style={{ fontSize: 13, fontWeight: 300, color: '#5A5855', textAlign: 'center', maxWidth: 320, marginBottom: 24 }}>Create your first project with Magic Mode</p>
                <button onClick={() => setMagicDialogOpen(true)} className="flex items-center gap-2 h-11 px-6 cursor-pointer" style={{ background: 'var(--orbis-blue)', color: '#1A1A1A', borderRadius: 100, fontSize: 13, fontWeight: 400, fontFamily: 'var(--orbis-font)', border: 'none' }}>
                  <Sparkles className="w-3.5 h-3.5" /> Start with Magic Mode
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project, index) => (
                  <div key={project.id} className="animate-fade-in opacity-0" style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}>
                    <ProjectCard project={project} onDelete={handleDeleteProject} onNavigate={handleNavigateToProject} accent={getCardAccent(project)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* ── Bottom navigation bar (mobile only) ── */}
      <div
        className="fixed bottom-0 left-0 right-0 md:hidden flex z-40"
        style={{
          background: 'var(--orbis-s1)',
          borderTop: '0.5px solid var(--orbis-border)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {[
          { icon: HomeIcon, label: 'Home', tab: 'home' },
          { icon: FolderOpen, label: 'Projects', tab: 'projects' },
          { icon: Layers, label: 'Templates', tab: 'templates' },
        ].map(item => {
          const isActive = activeTab === item.tab;
          return (
            <button
              key={item.tab}
              onClick={() => { setActiveTab(item.tab); if (item.tab === 'templates') navigate('/templates'); }}
              className="flex flex-col items-center justify-center gap-1 cursor-pointer transition-opacity"
              style={{
                flex: 1, padding: '10px 0',
                background: 'none', border: 'none',
                color: isActive ? 'var(--orbis-blue)' : 'var(--orbis-t4)',
              }}
            >
              <item.icon style={{ width: 20, height: 20 }} />
              <span style={{ fontSize: 10, fontFamily: 'var(--orbis-font)', fontWeight: isActive ? 500 : 400 }}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
